import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import useCarousel from '@src/hooks/useCarousel';

// BrochureAlumniCarousel — A dedicated component matching the original Figma
// design for displaying Crio alumni. Presents a single graduate card at a
// time with an auto-sliding paginated dot indicator.
const BrochureAlumniCarousel = ({ data = [] }) => {
  const { currentIndex, goToSlide } = useCarousel(data.length, 3000);

  if (!data || data.length === 0) return null;

  const currentItem = data[currentIndex];

  // Extract gatsbyImageData safely
  const profileImage = getImage(currentItem?.profileImage?.localFile);
  const companyLogo = getImage(currentItem?.companyLogo?.localFile);

  return (
    <div className="relative w-full rounded-2xl bg-[#effcf8] p-6 shadow-sm">
      <div className="flex flex-col items-center justify-center">
        {/* Profile Image with Yellow Ring */}
        <div className="mb-4 flex h-[80px] w-[80px] shrink-0 items-center justify-center overflow-hidden rounded-full border-[3px] border-[#FFB800] bg-white shadow-sm">
          {profileImage ? (
            <GatsbyImage
              image={profileImage}
              alt={currentItem.name || 'Alumni'}
              className="h-full w-full object-cover"
              imgClassName="rounded-full"
            />
          ) : (
            <img
              src={currentItem?.profileImage?.url || currentItem?.profileImage}
              alt={currentItem.name || 'Alumni'}
              className="h-full w-full rounded-full object-cover"
            />
          )}
        </div>

        {/* Text Details */}
        <h3 className="mb-1 text-center font-manrope text-[18px] font-bold text-v5-neutral-700">
          {currentItem.name}
        </h3>
        <span className="mb-4 text-center font-rubik text-sm text-v5-neutral-400">
          is in
        </span>

        {/* Company Logo */}
        <div className="mb-6 flex h-14 w-full items-center justify-center">
          {companyLogo ? (
            <GatsbyImage
              image={companyLogo}
              alt="Company logo"
              className="h-full w-full max-w-[180px] mix-blend-multiply"
              objectFit="contain"
            />
          ) : (
            <img
              src={currentItem?.companyLogo?.url || currentItem?.companyLogo}
              alt="Company logo"
              className="max-h-full max-w-[180px] object-contain object-center mix-blend-multiply"
            />
          )}
        </div>

        {/* Carousel Indicators (Dots) */}
        <div className="flex items-center justify-center gap-1.5">
          {data.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => goToSlide(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? 'w-6 bg-v5-green-600'
                  : 'w-2 bg-v5-neutral-300 hover:bg-v5-neutral-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrochureAlumniCarousel;
